/**
 * useInfluencerProfile — fetches the logged-in user's influencer profile and
 * caches the result in a module-level Zustand store so multiple pages share
 * the same request.
 *
 * `profile === null` means the user is logged in but NOT registered as an
 * influencer (backend 404/403). `profile === undefined` means we haven't
 * checked yet.
 */

import { useEffect } from 'react'
import { create } from 'zustand'
import { useAuthStore } from '@/stores/useAuthStore'
import influencerApi, { type InfluencerProfile } from '@/lib/api/influencerApi'

interface InfluencerProfileState {
  profile: InfluencerProfile | null | undefined
  loading: boolean
  error: string | null
  lastFetchedForUserId: string | null
  fetch: (userId: string, force?: boolean) => Promise<void>
  setProfile: (p: InfluencerProfile) => void
  reset: () => void
}

const useStore = create<InfluencerProfileState>((set, get) => ({
  profile: undefined,
  loading: false,
  error: null,
  lastFetchedForUserId: null,

  fetch: async (userId: string, force = false) => {
    const state = get()
    if (!force && state.lastFetchedForUserId === userId && !state.loading) return
    set({ loading: true, error: null })
    try {
      const data = await influencerApi.getMyProfile()
      set({ profile: data, loading: false, lastFetchedForUserId: userId })
    } catch (e) {
      set({ loading: false, error: (e as Error).message || 'Failed to load profile' })
    }
  },

  setProfile: (p) => set({ profile: p }),
  reset: () => set({ profile: undefined, loading: false, error: null, lastFetchedForUserId: null }),
}))

export interface UseInfluencerProfileResult {
  profile: InfluencerProfile | null | undefined
  loading: boolean
  error: string | null
  /** true once we've checked the server AND the user has a profile. */
  isInfluencer: boolean
  /** true once we've checked the server and confirmed no profile exists. */
  isNotRegistered: boolean
  /** Manually re-fetch. */
  refetch: () => Promise<void>
  /** Update cache after a PUT/PATCH. */
  setProfile: (p: InfluencerProfile) => void
}

export function useInfluencerProfile(): UseInfluencerProfileResult {
  const user = useAuthStore((s) => s.user)
  const { profile, loading, error, fetch, setProfile, reset } = useStore()

  useEffect(() => {
    if (!user?.uid) {
      reset()
      return
    }
    fetch(user.uid)
  }, [user?.uid, fetch, reset])

  const isInfluencer = !!profile
  const isNotRegistered = profile === null

  return {
    profile,
    loading,
    error,
    isInfluencer,
    isNotRegistered,
    refetch: async () => {
      if (user?.uid) await fetch(user.uid, true)
    },
    setProfile,
  }
}

export default useInfluencerProfile
